import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '@shared/ui';
import { saveSessionSenses } from '../api/wordsApi';

/**
 * Карточки одной части речи слова (state): каждый смысл state'а — отдельная карточка
 * в тренировке (2026-09-25). После создания карточку можно только удалить: удаление
 * снимает смысл со state'а, а последняя карточка — это всё слово, её удаляет
 * onRemoveLast. Без onRemoveLast (чужая, скрытая, ещё создаётся) — только просмотр.
 * Удаления сохраняются по очереди; ошибка возвращает прежний список.
 */
export function useSessionSenses(
  stateId: string | undefined,
  serverIds: string[],
  {
    onSaved,
    onRemoveLast,
  }: { onSaved?: (ids: string[]) => void; onRemoveLast?: () => void } = {},
) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [ids, setIds] = useState<string[]>(serverIds);
  const pending = useRef(0);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const serverKey = serverIds.join(',');

  // Свежие данные с сервера (перезапрос списка) — только когда своих сохранений нет.
  useEffect(() => {
    if (pending.current === 0) setIds(serverKey ? serverKey.split(',') : []);
  }, [serverKey]);

  const editable = Boolean(stateId) && !stateId!.includes(':') && Boolean(onRemoveLast);

  const remove = (senseId: string) => {
    if (!editable || !ids.includes(senseId)) return;
    if (ids.length === 1) {
      onRemoveLast?.();
      return;
    }
    const previous = ids;
    const next = ids.filter((id) => id !== senseId);
    setIds(next);
    pending.current += 1;
    queue.current = queue.current
      .then(() => saveSessionSenses(stateId!, next))
      .then((saved) => {
        onSaved?.(saved);
        void qc.invalidateQueries({ queryKey: ['words'] });
      })
      .catch(() => {
        setIds(previous);
        toast({ tone: 'error', message: t('sessionSenses.saveError') });
      })
      .finally(() => {
        pending.current -= 1;
      });
  };

  return { ids, remove: editable ? remove : undefined };
}
